import { ExistingCollectionObservationForm } from "@/components/observations/ExistingCollectionObservationForm"
import { getCollectionQueryOptions } from "@/hooks/useCollection"
import { useObservation } from "@/hooks/useObservation"
import { queryClient } from "@/lib/queryClient"
import { Spinner } from "@fieldbook/ui/spinner"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@fieldbook/ui/alert-dialog"
import { createFileRoute, ErrorComponentProps } from "@tanstack/react-router"
import * as z from "zod"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"

const searchSchema = z.object({
  siteId: z.string().optional(),
  collectionId: z.string().optional(),
  duplicateFromId: z.string().optional(),
  draftId: z.string().uuid().optional(),
})

export const Route = createFileRoute(
  "/_private/projects/$id/observations/new/existing",
)({
  component: NewCollectionObservation,
  validateSearch: (search) => searchSchema.parse(search),
  loaderDeps: ({ search: { collectionId } }) => ({ collectionId }),
  loader: async ({ deps: { collectionId } }) => {
    if (!collectionId) return {}
    try {
      await queryClient.ensureQueryData(getCollectionQueryOptions(collectionId))
    } catch (e) {
      // noop
    }
    return {}
  },
  errorComponent: ErrorComponent,
})

function ErrorComponent({ error }: ErrorComponentProps) {
  const nav = Route.useNavigate()

  return (
    <AlertDialog open={true}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Error</AlertDialogTitle>
          <AlertDialogDescription className="flex flex-col gap-2 text-lg">
            {error.message}
          </AlertDialogDescription>

          <AlertDialogCancel onClick={() => nav({ to: "/projects/$id" })}>
            Close
          </AlertDialogCancel>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function NewCollectionObservation() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId } = Route.useParams()
  const { siteId, collectionId, duplicateFromId, draftId } = Route.useSearch()

  const { data: duplicateFrom, isPending: isDuplicatePending } = useObservation(
    { observationId: duplicateFromId ?? "", projectId },
  )

  if (duplicateFromId && isDuplicatePending) {
    return <Spinner />
  }

  return (
    <ExistingCollectionObservationForm
      projectId={projectId}
      siteId={siteId}
      collectionId={collectionId}
      duplicateFrom={duplicateFromId ? (duplicateFrom ?? undefined) : undefined}
      draftId={draftId}
    />
  )
}
